/**
 * Category Management Context
 * API-backed CRUD for treatment categories - reads from /api/public/treatment-taxonomy
 * Source of truth: MongoDB treatment_categories collection (via backend API)
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import api from '../utils/api';

const CategoryManagementContext = createContext();

export const useCategoryManagement = () => {
  const context = useContext(CategoryManagementContext);
  if (!context) {
    throw new Error('useCategoryManagement must be used within CategoryManagementProvider');
  }
  return context;
};

// Emoji mapping for known category slugs
const SLUG_EMOJI_MAP = {
  aesthetics: '✨',
  dermatology: '☀️',
  dental: '🦷',
  bariatric: '⚖️',
  orthopedic: '🦴',
  wellness: '💆',
  cosmetic: '💉',
  hair: '💇',
  ophthalmology: '👁️',
  cardiac: '💓',
  fertility: '👶',
};

export const AVAILABLE_ICONS = [
  { id: 'stethoscope', name: 'Stethoscope', emoji: '🏥' },
  { id: 'heart', name: 'Heart', emoji: '❤️' },
  { id: 'smile', name: 'Smile/Dental', emoji: '🦷' },
  { id: 'eye', name: 'Eye', emoji: '👁️' },
  { id: 'scissors', name: 'Scissors/Hair', emoji: '💇' },
  { id: 'sparkles', name: 'Cosmetic', emoji: '✨' },
  { id: 'baby', name: 'Fertility', emoji: '👶' },
  { id: 'bone', name: 'Orthopedic', emoji: '🦴' },
  { id: 'brain', name: 'Neurology', emoji: '🧠' },
  { id: 'activity', name: 'Cardiac', emoji: '💓' },
  { id: 'pill', name: 'General Medical', emoji: '💊' },
  { id: 'scale', name: 'Bariatric', emoji: '⚖️' },
  { id: 'user', name: 'Body Contouring', emoji: '💪' },
  { id: 'sun', name: 'Dermatology', emoji: '☀️' }
];

export const CategoryManagementProvider = ({ children }) => {
  const [categories, setCategories] = useState([]);
  const [loaded, setLoaded] = useState(false);

  // Fetch categories from backend taxonomy API (all statuses for admin)
  // P7-CLEANUP-6: skip admin probe when no auth token to keep anonymous console clean.
  const fetchCategories = useCallback(async () => {
    try {
      const hasToken = typeof window !== 'undefined' && (
        null
      );
      let data;
      if (hasToken) {
        try {
          const res = await api.get('/admin/treatment-taxonomy/categories');
          data = res.data;
        } catch {
          const res = await api.get('/public/treatment-taxonomy');
          data = res.data;
        }
      } else {
        const res = await api.get('/public/treatment-taxonomy');
        data = res.data;
      }
      const rawCats = data.categories || [];
      const cats = rawCats.map(c => ({
        id: c.slug,
        name: c.name,
        emoji: SLUG_EMOJI_MAP[c.slug] || '📋',
        description: c.description || '',
        image: c.image_url || '',
        image_url: c.image_url || '',
        media_id: c.media_id || null,
        status: c.status || 'active',
        sort_order: c.sort_order,
        treatmentCount: c.treatment_count || 0,
      }));
      setCategories(cats);
    } catch (e) {
      console.error('Failed to load categories from API:', e);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const getAllCategories = useCallback(() => categories, [categories]);

  const getActiveCategories = useCallback(() =>
    categories.filter(c => c.status === 'active'), [categories]);

  const getCategoryById = useCallback((id) =>
    categories.find(c => c.id === id), [categories]);

  const getCategoryDisplay = useCallback((categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    if (!cat) return { id: categoryId, name: categoryId, emoji: '📋' };
    return cat;
  }, [categories]);

  const addCategory = useCallback(async (data) => {
    try {
      const enName = typeof data.name === 'object' ? (data.name?.en || '') : (data.name || '');
      await api.post('/admin/treatment-taxonomy/categories/upsert', {
        slug: enName.toLowerCase().replace(/\s+/g, '_'),
        name: data.name,
        description: data.description || {},
        sort_order: 99,
        image_url: data.image || null,
        media_id: data.media_id || null,
      });
      await fetchCategories();
    } catch (e) { console.error('Failed to add category:', e); }
  }, [fetchCategories]);

  const updateCategory = useCallback(async (id, data) => {
    try {
      await api.post('/admin/treatment-taxonomy/categories/upsert', {
        slug: id,
        name: data.name,
        description: data.description || {},
        image_url: data.image || null,
        media_id: data.media_id || null,
      });
      if (data.media_id || data.image) {
        await api.put(`/admin/catalog/categories/${id}/image`, {
          image_url: data.image || null, media_id: data.media_id || null,
        });
      }
      await fetchCategories();
    } catch (e) { console.error('Failed to update category:', e); }
  }, [fetchCategories]);

  const deleteCategory = useCallback(async (slug) => {
    try {
      await api.post('/admin/treatment-taxonomy/categories/disable', { slug });
      await fetchCategories();
    } catch (e) { console.error('Failed to delete category:', e); }
  }, [fetchCategories]);
  const updateTreatmentCount = useCallback(() => {}, []);

  const value = {
    categories,
    loaded,
    getAllCategories,
    getActiveCategories,
    getCategoryById,
    getCategoryDisplay,
    addCategory,
    updateCategory,
    deleteCategory,
    updateTreatmentCount,
    fetchCategories,
    AVAILABLE_ICONS
  };

  return (
    <CategoryManagementContext.Provider value={value}>
      {children}
    </CategoryManagementContext.Provider>
  );
};

export default CategoryManagementContext;
